import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Grid, IconButton } from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import SydLabel from '../../../../../../../../commonDesign/SydLabel'
import SydSelect from '../../../../../../../../commonDesign/SydSelect'
import SydInput from '../../../../../../../../commonDesign/SydInput'
import SydButton from '../../../../../../../../commonDesign/Button'

const EMPTY_ARR = []

function RingChartEditor ({ config, columns, onChange }) {
  const metrics = useMemo(() => config.metrics || EMPTY_ARR, [config.metrics])

  const columnOptions = useMemo(() => {
    return [
      { label: 'Select a column...', value: '__placeholder__' },
      ...columns.map(col => ({ label: col.name, value: col.name }))
    ]
  }, [columns])

  const handleChange = useCallback((field, value) => {
    onChange({ [field]: value })
  }, [onChange])

  const handleMetricChange = useCallback((idx, field, value) => {
    const updated = [...metrics]
    updated[idx] = { ...updated[idx], [field]: value }
    onChange({ metrics: updated })
  }, [metrics, onChange])

  const handleAddMetric = useCallback(() => {
    onChange({
      metrics: [...metrics, { accessor: null, name: null, target: null }]
    })
  }, [metrics, onChange])

  const handleRemoveMetric = useCallback((idx) => {
    onChange({ metrics: metrics.filter((_, i) => i !== idx) })
  }, [metrics, onChange])

  return (
    <Grid container spacing={2}>
      <Grid item xs={6} md={3}>
        <SydLabel label='Width (px)'>
          <SydInput
            minWidth='0'
            type='number'
            value={config.width || 300}
            onChange={(e) => handleChange('width', Number(e.target.value) || 300)}
          />
        </SydLabel>
      </Grid>
      <Grid item xs={6} md={3}>
        <SydLabel label='Value Format'>
          <SydInput
            minWidth='0'
            value={config.valueFormat || 'percentage'}
            onChange={(e) => handleChange('valueFormat', e.target.value || 'percentage')}
            placeholder='percentage'
          />
        </SydLabel>
      </Grid>

      <Grid item xs={12}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <h4 style={{ margin: 0 }}>Metrics</h4>
          <SydButton size='sm' variant='primary' onClick={handleAddMetric}>
            + Add Metric
          </SydButton>
        </div>

        {metrics.length === 0 && (
          <div style={{ color: 'var(--text-color-subtle)', fontSize: '0.875rem' }}>
            No metrics configured. Add a metric to define ring gauge data.
          </div>
        )}

        {metrics.map((m, idx) => (
          <div
            key={m.accessor || idx}
            style={{
              border: '1px solid var(--border-color)',
              borderRadius: 'var(--border-radius-sm)',
              padding: '12px',
              marginBottom: '8px'
            }}
          >
            <Grid container spacing={2} alignItems='flex-end'>
              <Grid item xs={6} md={3}>
                <SydLabel required label='Value Column'>
                  <SydSelect
                    minWidth='0'
                    options={columnOptions}
                    value={m.accessor || '__placeholder__'}
                    onChange={(e) => handleMetricChange(idx, 'accessor', e.target.value === '__placeholder__' ? null : e.target.value)}
                  />
                </SydLabel>
              </Grid>
              <Grid item xs={6} md={3}>
                <SydLabel label='Name'>
                  <SydInput
                    minWidth='0'
                    value={m.name || ''}
                    onChange={(e) => handleMetricChange(idx, 'name', e.target.value || null)}
                    placeholder='Optional label'
                  />
                </SydLabel>
              </Grid>
              <Grid item xs={6} md={3}>
                <SydLabel label='Target' helperText='Number or column name for percentage'>
                  <SydInput
                    minWidth='0'
                    value={m.target ?? ''}
                    onChange={(e) => {
                      const v = e.target.value
                      const num = Number(v)
                      handleMetricChange(idx, 'target', v === '' ? null : (isNaN(num) ? v : num))
                    }}
                    placeholder='1 or columnName'
                  />
                </SydLabel>
              </Grid>
              <Grid item xs={6} md={3}>
                <IconButton
                  size='small'
                  onClick={() => handleRemoveMetric(idx)}
                  style={{ color: 'var(--color-danger-500)' }}
                >
                  <DeleteIcon fontSize='small' />
                </IconButton>
              </Grid>
            </Grid>
          </div>
        ))}
      </Grid>
    </Grid>
  )
}

RingChartEditor.propTypes = {
  config: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired
}

export default RingChartEditor
